import { cx } from "../cx";
import type { DepartmentFieldAccess } from "../departmentFields";
import type { ExtractFillMap } from "../extractFill";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import type { FormState, RouteDirection } from "../types";
import type { Errors } from "../validation";
import { CountrySelect } from "./CountrySelect";
import { TextInput } from "./fields";

class RouteSideFields {
  constructor(private readonly direction: RouteDirection) {}

  countryId(): "loadCountry" | "unloadCountry" {
    return this.direction === "load" ? "loadCountry" : "unloadCountry";
  }

  postalId(): "loadPostal" | "unloadPostal" {
    return this.direction === "load" ? "loadPostal" : "unloadPostal";
  }

  cityId(): "loadCity" | "unloadCity" {
    return this.direction === "load" ? "loadCity" : "unloadCity";
  }

  streetId(): "loadStreet" | "unloadStreet" {
    return this.direction === "load" ? "loadStreet" : "unloadStreet";
  }

  houseId(): "loadHouseNumber" | "unloadHouseNumber" {
    return this.direction === "load" ? "loadHouseNumber" : "unloadHouseNumber";
  }

  country(form: FormState): string {
    return this.direction === "load" ? form.loadCountry : form.unloadCountry;
  }

  postal(form: FormState): string {
    return this.direction === "load" ? form.loadPostal : form.unloadPostal;
  }

  city(form: FormState): string {
    return this.direction === "load" ? form.loadCity : form.unloadCity;
  }

  street(form: FormState): string {
    return this.direction === "load" ? form.loadStreet : form.unloadStreet;
  }

  house(form: FormState): string {
    return this.direction === "load" ? form.loadHouseNumber : form.unloadHouseNumber;
  }

  patchCountry(value: string): Partial<FormState> {
    return this.direction === "load" ? { loadCountry: value } : { unloadCountry: value };
  }

  patchPostal(value: string): Partial<FormState> {
    return this.direction === "load" ? { loadPostal: value } : { unloadPostal: value };
  }

  patchCity(value: string): Partial<FormState> {
    return this.direction === "load" ? { loadCity: value } : { unloadCity: value };
  }

  patchStreet(value: string): Partial<FormState> {
    return this.direction === "load" ? { loadStreet: value } : { unloadStreet: value };
  }

  patchHouse(value: string): Partial<FormState> {
    return this.direction === "load" ? { loadHouseNumber: value } : { unloadHouseNumber: value };
  }
}

export class HomeCountryRoutePatch {
  constructor(private readonly fields: DepartmentFieldAccess) {}

  apply(direction: RouteDirection, form: FormState, nextCountry: string): Partial<FormState> {
    const side = new RouteSideFields(direction);
    const other = new RouteSideFields(direction === "load" ? "unload" : "load");
    const patch = side.patchCountry(nextCountry);
    const home = this.fields.homeCountry();
    if (!home) return patch;
    const next = nextCountry.trim().toUpperCase();
    const otherCode = other.country(form).trim().toUpperCase();
    if (!next || next === home || !otherCode || otherCode === home) return patch;
    return { ...patch, ...other.patchCountry("") };
  }
}

interface Props {
  direction: RouteDirection;
  form: FormState;
  errors: Errors;
  highlighted: Set<string>;
  extractFill?: ExtractFillMap;
  fields: DepartmentFieldAccess;
  onPatch: (patch: Partial<FormState>) => void;
  onBlur: (id: string) => void;
}

export function RouteColumn({
  direction,
  form,
  errors,
  highlighted,
  extractFill,
  fields,
  onPatch,
  onBlur,
}: Props) {
  const copy = useQuoteCopy();
  const side = new RouteSideFields(direction);
  const other = new RouteSideFields(direction === "load" ? "unload" : "load");
  const countryId = side.countryId();
  const postalId = side.postalId();
  const cityId = side.cityId();
  const streetId = side.streetId();
  const houseId = side.houseId();
  const flash = (id: string) => highlighted.has(id) && "flash";
  const countryStatus = extractFill?.status(countryId);
  const countryFill = countryStatus === "missing" && side.country(form).trim() ? undefined : countryStatus;

  return (
    <div className="nv-route-col">
      <div className="nv-route-tag">{direction === "load" ? copy.t("route.loading") : copy.t("route.unloading")}</div>
      <CountrySelect
        id={`nv-${countryId}`}
        required={fields.isRequired(countryId)}
        error={errors[countryId]}
        fillStatus={countryFill}
        className={cx(flash(countryId))}
        countries={fields.countriesForSide(other.country(form))}
        value={side.country(form)}
        onChange={(v) => onPatch(new HomeCountryRoutePatch(fields).apply(direction, form, v))}
        onBlur={() => onBlur(countryId)}
      />
      <TextInput
        id={`nv-${postalId}`}
        label={copy.t("route.postcode")}
        placeholder={copy.t("route.postcodePlaceholder")}
        required={fields.isRequired(postalId)}
        error={errors[postalId]}
        fillStatus={extractFill?.status(postalId)}
        className={cx(flash(postalId))}
        value={side.postal(form)}
        onChange={(v) => onPatch(side.patchPostal(v))}
        onBlur={() => onBlur(postalId)}
      />
      <TextInput
        id={`nv-${cityId}`}
        label={copy.t("route.city")}
        placeholder={copy.t("route.cityPlaceholder")}
        required={fields.isRequired(cityId)}
        error={errors[cityId]}
        fillStatus={extractFill?.status(cityId)}
        className={cx(flash(cityId))}
        value={side.city(form)}
        onChange={(v) => onPatch(side.patchCity(v))}
        onBlur={() => onBlur(cityId)}
      />
      {fields.isPresent(streetId) && (
        <TextInput
          id={`nv-${streetId}`}
          label={copy.t("route.street")}
          placeholder={copy.t("route.streetPlaceholder")}
          required={fields.isRequired(streetId)}
          error={errors[streetId]}
          fillStatus={extractFill?.status(streetId)}
          className={cx(flash(streetId))}
          value={side.street(form)}
          onChange={(v) => onPatch(side.patchStreet(v))}
          onBlur={() => onBlur(streetId)}
        />
      )}
      {fields.isPresent(houseId) && (
        <TextInput
          id={`nv-${houseId}`}
          label={copy.t("route.house")}
          placeholder={copy.t("route.housePlaceholder")}
          required={fields.isRequired(houseId)}
          error={errors[houseId]}
          fillStatus={extractFill?.status(houseId)}
          className={cx(flash(houseId))}
          value={side.house(form)}
          onChange={(v) => onPatch(side.patchHouse(v))}
          onBlur={() => onBlur(houseId)}
        />
      )}
    </div>
  );
}
